import React, { useEffect } from 'react';
import {
  Table,
  Pagination,
  Button,
  Icon,
  Input,
  Select,
  Dimmer,
  Loader,
} from 'semantic-ui-react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import {
  getAllInternalUsers,
  openEditInternalUserModalWindow,
  getAllRoles,
  openDeleteInternalUserModalWindow,
  setInternalUserInputSearch,
  clearInternalUserInputSearch,
  setInternalUserRoleSearch,
  searchInternalUser,
  changeInternalUsersTableSortState,
} from 'state/access/actions';
import {
  ASCENDING,
  DESCENDING,
} from 'constants/access/internalUsersTableSortMethod';

const ListUsers = props => {
  const {
    dispatch,
    internalUsers,
    allRoles,
    inputSearch,
    searchingInternalUser,
    roleSearch,
    internalUserTableSortState,
  } = props;
  const { data, paging } = internalUsers;
  const { users } = data || [];
  let totalPages = 1;
  if (paging) totalPages = Math.ceil(paging.total / paging.size);

  useEffect(() => {
    dispatch(getAllRoles({ initialState: [] }));
  }, [dispatch]);

  // eslint-disable-next-line no-unused-vars
  const handleOnPageChange = (_, val) => {
    const { activePage } = val;
    dispatch(
      getAllInternalUsers({
        email: inputSearch,
        roleId: roleSearch.id,
        page: activePage,
        size: 10,
        sort: internalUserTableSortState.sort,
      })
    );
  };

  const handleEditButton = internalUser => {
    dispatch(openEditInternalUserModalWindow({ selectedUser: internalUser }));
    dispatch(getAllRoles({ initialState: internalUser.roles }));
  };

  const handleDeleteButton = internalUser => {
    dispatch(openDeleteInternalUserModalWindow({ selectedUser: internalUser }));
  };

  const getAllRolesName = () => {
    const allRolesName = [{ key: 0, text: 'all', value: 'all' }];
    allRoles.forEach(role =>
      allRolesName.push({ key: role.id, text: role.name, value: role.name })
    );
    return allRolesName;
  };

  const handleClearInputSearch = () => {
    const clearPayload = {
      page: 1,
      size: 10,
      email: '',
      roleId: roleSearch.id,
      sort: internalUserTableSortState.sort,
    };
    dispatch(clearInternalUserInputSearch(clearPayload));
  };

  const handleOnInputSearchChange = (e, { value }) => {
    if (value === '') {
      handleClearInputSearch();
    } else dispatch(setInternalUserInputSearch(value));
  };

  const handleOnSubmitInputSearch = () => {
    const searchPayload = {
      page: 1,
      size: 10,
      email: inputSearch,
      roleId: roleSearch.id,
      sort: internalUserTableSortState.sort,
    };
    dispatch(searchInternalUser(searchPayload));
  };

  const handleKeyPress = e => {
    // when enter key pressed
    if (e.charCode === 13) {
      handleOnSubmitInputSearch();
    }
  };

  const handleOnRoleSelected = (_, { value }) => {
    let selectedRole = allRoles.filter(role => role.name === value)[0];
    if (!selectedRole) selectedRole = {};
    dispatch(setInternalUserRoleSearch(selectedRole));
  };

  const handleSortByEmailButtonOnClick = () => {
    const payload = {
      page: paging.page,
      size: 10,
      email: inputSearch,
      roleId: roleSearch.id,
      sort: {
        column: 'email',
      },
    };
    switch (internalUserTableSortState.sort.direction) {
      case ASCENDING:
        payload.sort.direction = DESCENDING;
        break;
      case DESCENDING:
        payload.sort.direction = ASCENDING;
        break;
      default:
        break;
    }
    dispatch(changeInternalUsersTableSortState(payload));
    dispatch(getAllInternalUsers(payload));
  };

  const handleSortByDateButtonOnClick = () => {
    const payload = {
      page: paging.page,
      size: 10,
      email: inputSearch,
      roleId: roleSearch.id,
      sort: {
        column: 'createdAt',
      },
    };
    switch (internalUserTableSortState.sort.direction) {
      case ASCENDING:
        payload.sort.direction = DESCENDING;
        break;
      case DESCENDING:
        payload.sort.direction = ASCENDING;
        break;
      default:
        break;
    }
    dispatch(changeInternalUsersTableSortState(payload));
    dispatch(getAllInternalUsers(payload));
  };

  const formatCreatedAtDate = date => format(new Date(date), 'yyyy-MM-dd pp');

  return (
    <>
      <Dimmer active={searchingInternalUser}>
        <Loader size="massive">Searching</Loader>
      </Dimmer>
      <h3>All Users</h3>
      <Input
        icon
        type="text"
        onChange={handleOnInputSearchChange}
        onKeyPress={handleKeyPress}
        placeholder="Search User By Email..."
        action
      >
        {inputSearch !== '' && (
          <Icon name="delete" link onClick={handleClearInputSearch} />
        )}
        <input value={inputSearch} style={{ minWidth: '100%' }} />
        <Select
          onChange={handleOnRoleSelected}
          options={getAllRolesName()}
          defaultValue="all"
          style={{ minWidth: '220px' }}
        />
        <Button type="submit" onClick={handleOnSubmitInputSearch}>
          Search
        </Button>
      </Input>
      <Table celled selectable sortable size="small">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell
              sorted={
                internalUserTableSortState.sort.column === 'email'
                  ? internalUserTableSortState.sort.direction
                  : null
              }
              onClick={handleSortByEmailButtonOnClick}
            >
              Email
            </Table.HeaderCell>
            <Table.HeaderCell>Roles</Table.HeaderCell>
            <Table.HeaderCell
              sorted={
                internalUserTableSortState.sort.column === 'createdAt'
                  ? internalUserTableSortState.sort.direction
                  : null
              }
              onClick={handleSortByDateButtonOnClick}
            >
              Created At
            </Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {users ? (
            <>
              {users.map(internalUser => (
                <Table.Row key={internalUser.id}>
                  <Table.Cell key={internalUser.email} collapsing>
                    {internalUser.email}
                  </Table.Cell>
                  <Table.Cell
                    key={internalUser.id}
                    style={{ maxWidth: '550px' }}
                  >
                    {(() => {
                      if (internalUser.roles.length === 0) return 'No Roles';
                      const roleNames = internalUser.roles.map(role => {
                        const out = `${role.name}`;
                        return out;
                      });
                      return roleNames.join(' | ');
                    })()}
                  </Table.Cell>
                  <Table.Cell key={internalUser.created_at}>
                    {formatCreatedAtDate(internalUser.created_at)}
                  </Table.Cell>
                  <Table.Cell collapsing key={`${internalUser.id}-action`}>
                    <Button
                      primary
                      inverted
                      compact
                      key={`${internalUser.id}-action-edit`}
                      onClick={() => handleEditButton(internalUser)}
                    >
                      <Icon name="edit" />
                      Edit
                    </Button>
                    <Button
                      color="red"
                      inverted
                      compact
                      key={`${internalUser.id}-action-delete`}
                      onClick={() => handleDeleteButton(internalUser)}
                    >
                      <Icon name="user delete" />
                      Delete
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </>
          ) : (
            <></>
          )}
        </Table.Body>

        {paging ? (
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan="100%">
                <Pagination
                  activePage={paging.page}
                  totalPages={totalPages}
                  onPageChange={handleOnPageChange}
                  size="small"
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        ) : (
          <></>
        )}
      </Table>
    </>
  );
};

const mapStateToProps = ({ access }) => ({
  internalUsers: access.internalUsers,
  allRoles: access.allRoles,
  editModal: access.editModal,
  inputSearch: access.inputSearch,
  searchingInternalUser: access.searchingInternalUser,
  roleSearch: access.roleSearch,
  internalUserTableSortState: access.internalUserTableSortState,
});

ListUsers.propTypes = {
  dispatch: PropTypes.func.isRequired,
  internalUsers: PropTypes.object.isRequired,
  allRoles: PropTypes.array.isRequired,
  inputSearch: PropTypes.string.isRequired,
  searchingInternalUser: PropTypes.bool.isRequired,
  roleSearch: PropTypes.object.isRequired,
  internalUserTableSortState: PropTypes.object.isRequired,
};

export default connect(mapStateToProps)(ListUsers);
